/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { CopyButton } from '@strata/react-clip';
import { FormSection } from '@strata/react-form-section';
import { Grid, SecondaryData } from '@strata/react-grid';
import { DetailLabel, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { Spinner } from '@strata/react-loading-animation';
import { MessagingBlock } from '@strata/react-messaging-block';
import { formatRateString } from '@strata/react-servicing-rate';
import { Badge, Icon } from '@strata/react-status-icons';
import { Tooltip } from '@strata/react-tooltip';
import PropTypes from 'prop-types';

import { cleanGPI } from 'services/treatmentLockHelpers';
import { useCheckLockStatusV2Query } from 'store/api/pbmApi';
import {
    useGetChildSupplierNpisByNetworkQuery,
    useGetNdcByGpiQuery,
    useGetSupplierDetailsQuery,
} from 'store/api/referenceApi';

const HOVER_CLOSE_DELAY_MS = 1000;
const NETWORK_PHARMACY_IDENTIFIER_TYPE = 'NETWORK';
const NDC_PRODUCT_IDENTIFIER_TYPE = 'NDC';
const CONTRACT = 'CONTRACT';

const ArrayCell = ({ items }) => (
    <SecondaryData
        customTooltipContent={null}
        data={
            <Tooltip
                hoverCloseDelay={HOVER_CLOSE_DELAY_MS}
                content={
                    <>
                        {items
                            .map((item) => item.trim())
                            .sort(sortAsc)
                            .map((item) => (
                                <div key={item}>{item}</div>
                            ))}
                    </>
                }
            >
                {items.join(', ')}
            </Tooltip>
        }
    />
);
ArrayCell.propTypes = {
    items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const createCopyTextForToolTipping = (data) => data?.map((item) => item || '').join(', ');

const Tooltipping = ({ data }) => data?.map((item) => <div key={item || ''}>{item || ''}</div>);

const TooltippingWithCopy = ({ data }) => (
    <>
        <div className="margin__bottom--small">
            <Tooltipping data={data} />
        </div>
        <CopyButton associatedText={createCopyTextForToolTipping(data)} />
    </>
);

TooltippingWithCopy.propTypes = {
    data: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const AssignedPharmacyCell = ({ getValue, row }) => {
    const pharmacyIdentifiers = getValue();
    const pharmacyIdentifierType = row.original.pharmacy_identifier_type;
    const isArray = Array.isArray(pharmacyIdentifiers);
    const isNetworkType = pharmacyIdentifierType === NETWORK_PHARMACY_IDENTIFIER_TYPE;
    const networkCode = isNetworkType ? pharmacyIdentifiers : undefined;
    const { data } = useGetChildSupplierNpisByNetworkQuery(
        networkCode ? { networkCode } : skipToken,
    );

    if (isArray) {
        return <ArrayCell items={pharmacyIdentifiers} />;
    }

    if (!isNetworkType) {
        return <SecondaryData data={pharmacyIdentifiers} />;
    }

    return (
        <SecondaryData
            customTooltipContent={null}
            data={
                <>
                    {networkCode}
                    {data && data.length > 0 && (
                        <Tooltip
                            content={<TooltippingWithCopy data={data} />}
                            hoverCloseDelay={HOVER_CLOSE_DELAY_MS}
                        >
                            <Icon
                                icon={IconNames.INFO_SIGN}
                                size={16}
                                intent={Icon.Intent.INFO}
                                title="Child Pharmacy NPIs"
                                className="padding__left--small"
                            />
                        </Tooltip>
                    )}
                </>
            }
        />
    );
};
AssignedPharmacyCell.propTypes = {
    getValue: PropTypes.func.isRequired,
    row: PropTypes.shape({
        original: PropTypes.shape({ pharmacy_identifier_type: PropTypes.string }),
    }).isRequired,
};

const AssociatedPANumbers = ({ paNumbers = undefined }) => {
    if (!paNumbers) {
        return null;
    }
    const pas = paNumbers.split(/,/);
    return pas.length <= 1 ? (
        paNumbers.trim()
    ) : (
        <Tooltip
            hoverCloseDelay={HOVER_CLOSE_DELAY_MS}
            content={
                <TooltippingWithCopy
                    data={[...new Set(pas.map((item) => item.trim()))].sort(sortAsc)}
                />
            }
        >
            <Badge
                count={pas.length}
                intent={Badge.Intent.STATUS_DETAIL}
                className="sds_pbm-lock-status__icon"
            />
        </Tooltip>
    );
};
AssociatedPANumbers.propTypes = {
    paNumbers: PropTypes.string,
};

const ProductIdentifierTypeWithGPITooltip = ({
    productIdentifierType,
    productIdentifier = undefined,
}) => {
    const isNDC = productIdentifierType === NDC_PRODUCT_IDENTIFIER_TYPE;
    const isArray = Array.isArray(productIdentifier);
    const gpi = isNDC || isArray ? undefined : cleanGPI(productIdentifier);
    const { data } = useGetNdcByGpiQuery(gpi || skipToken);

    if (isNDC || !data || data.length === 0) {
        return productIdentifierType;
    }

    return (
        <>
            {productIdentifierType}
            <Tooltip
                content={
                    <TooltippingWithCopy
                        data={[...new Set(data?.map((item) => item.ndc_upc_hri))].sort(sortAsc)}
                    />
                }
                hoverCloseDelay={HOVER_CLOSE_DELAY_MS}
            >
                <Icon
                    icon={IconNames.INFO_SIGN}
                    size={16}
                    intent={Icon.Intent.INFO}
                    title="NDCs for GPI"
                    className="padding__left--small"
                />
            </Tooltip>
        </>
    );
};
ProductIdentifierTypeWithGPITooltip.propTypes = {
    productIdentifierType: PropTypes.string.isRequired,
    productIdentifier: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};

const AssignedPharmacyIssues = ({
    activePharmacyData = undefined,
    pharmacyIdentifiers = undefined,
    pharmacyIdentifierType = undefined,
}) => {
    const hasActivePharmacyData = !!activePharmacyData;
    const isNetworkType = pharmacyIdentifierType === NETWORK_PHARMACY_IDENTIFIER_TYPE;
    const networkCode = isNetworkType ? pharmacyIdentifiers : undefined;
    const { data: networkChildNpis } = useGetChildSupplierNpisByNetworkQuery(
        hasActivePharmacyData && networkCode ? { networkCode } : skipToken,
    );
    const isArray = Array.isArray(pharmacyIdentifiers);

    let pIds;
    if (networkChildNpis && networkChildNpis.length > 0) {
        pIds = networkChildNpis;
    } else if (isArray) {
        pIds = pharmacyIdentifiers;
    } else {
        pIds = pharmacyIdentifiers ? pharmacyIdentifiers.split(',') : [];
    }
    pIds = pIds.map((inner) => inner.trim());
    if (
        !hasActivePharmacyData ||
        activePharmacyData?.childrenNpis?.find((item) => pIds.includes(item))
    ) {
        return '';
    }
    return (
        <Icon
            icon={IconNames.WARNING_SIGN}
            intent={Icon.Intent.WARNING}
            tooltipContent={`Assigned to ${activePharmacyData?.organizationName ?? '--'}'s Pharmacy`}
        />
    );
};
AssignedPharmacyIssues.propTypes = {
    activePharmacyData: PropTypes.shape({
        organizationName: PropTypes.string,
        childrenNpis: PropTypes.arrayOf(PropTypes.string),
    }),
    pharmacyIdentifiers: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
    ]),
    pharmacyIdentifierType: PropTypes.string,
};

const commonColumns = (activePharmacyData) => [
    {
        accessorKey: 'locked_rate',
        header: 'Rate',
        size: 121,
        cell: ({ getValue, row }) => {
            const value = getValue();
            return (
                <SecondaryData
                    data={
                        <span className="font-semi-bold">
                            {row.original.contract_rate
                                ? CONTRACT
                                : formatRateString({
                                      bidDecimal: value?.rate,
                                      referentPriceType: value?.referent_price_type,
                                  })}
                        </span>
                    }
                />
            );
        },
    },
    { accessorKey: 'price_schedule', header: 'Price Schedule', size: 121 },
    {
        accessorKey: 'pharmacy_identifiers',
        header: 'Assigned Pharmacy',
        size: 143,
        cell: AssignedPharmacyCell,
    },
    {
        accessorKey: 'assigned_pharmacy_issues',
        header: <Icon icon={IconNames.WARNING_SIGN} />,
        size: 48,
        cell: ({ row }) => (
            <SecondaryData
                data={
                    <AssignedPharmacyIssues
                        activePharmacyData={activePharmacyData}
                        pharmacyIdentifiers={row.original.pharmacy_identifiers}
                        pharmacyIdentifierType={row.original.pharmacy_identifier_type}
                    />
                }
                customTooltipContent={null}
            />
        ),
    },
    {
        accessorKey: 'carrier_id',
        header: 'CAG',
        size: 55,
        cell: ({ row, getValue }) => {
            const carrierId = getValue();

            if (!carrierId) {
                return <SecondaryData data={null} />;
            }

            return (
                <SecondaryData
                    data={
                        <Tooltip
                            content={
                                <>
                                    <div className="margin__bottom--small">
                                        <div>
                                            <span className="font-bold">Carrier: </span>
                                            <span>{carrierId}</span>
                                        </div>
                                        <div>
                                            <span className="font-bold">Account: </span>
                                            <span>{row.original.account_id}</span>
                                        </div>
                                        <div>
                                            <span className="font-bold">Group: </span>
                                            <span>{row.original.group_id}</span>
                                        </div>
                                    </div>
                                    <CopyButton
                                        associatedText={[
                                            `Carrier:  ${carrierId}`,
                                            `Account: ${row.original.account_id}`,
                                            `Group: ${row.original.group_id}`,
                                        ].join(', ')}
                                    />
                                </>
                            }
                            hoverCloseDelay={HOVER_CLOSE_DELAY_MS}
                        >
                            <Icon
                                icon={IconNames.INFO_SIGN}
                                size={16}
                                intent={Icon.Intent.INFO}
                                title="CAG Info"
                            />
                        </Tooltip>
                    }
                    customTooltipContent={null}
                />
            );
        },
    },
];

const ProductIdentifierCell = ({ getValue }) => {
    const value = getValue();
    if (Array.isArray(value)) {
        return <ArrayCell items={value} />;
    }
    return <SecondaryData data={value} />;
};
ProductIdentifierCell.propTypes = {
    getValue: PropTypes.func.isRequired,
};

const overrideColumns = (activePharmacyData) => [
    {
        accessorKey: 'product_identifier_type',
        header: 'Type',
        size: 84,
        cell: ({ getValue, row }) => (
            <SecondaryData
                customTooltipContent={null}
                data={
                    <ProductIdentifierTypeWithGPITooltip
                        productIdentifierType={getValue()}
                        productIdentifier={row.original?.product_identifier}
                    />
                }
            />
        ),
    },
    {
        accessorKey: 'product_identifier',
        header: 'Product',
        size: 153,
        cell: ProductIdentifierCell,
    },
    {
        accessorKey: 'override_start_date',
        header: 'Override Start',
        size: 130,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'override_expiration_date',
        header: 'Override Expiration',
        size: 152,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    { accessorKey: 'status', header: 'Override Status', size: 144 },
    {
        accessorKey: 'override_sent_at',
        header: 'Override Sent',
        size: 154,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'associated_pa_numbers',
        header: 'Associated PAs',
        size: 121,
        cell: ({ getValue }) => (
            <SecondaryData
                customTooltipContent={null}
                data={<AssociatedPANumbers paNumbers={getValue()} />}
            />
        ),
    },
    ...commonColumns(activePharmacyData),
];

const authColumns = (activePharmacyData) => [
    { accessorKey: 'prior_auth_number', header: 'PA Number', size: 133 },
    {
        accessorKey: 'product_identifier_type',
        header: 'PA Type',
        size: 106,
        cell: ({ getValue, row }) => (
            <SecondaryData
                customTooltipContent={null}
                data={
                    <ProductIdentifierTypeWithGPITooltip
                        productIdentifierType={getValue()}
                        productIdentifier={row?.original?.product_identifier}
                    />
                }
            />
        ),
    },
    {
        accessorKey: 'product_identifier',
        header: 'Product',
        size: 153,
        cell: ProductIdentifierCell,
    },
    {
        accessorKey: 'pa_start_date',
        header: 'PA Start',
        size: 105,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'pa_expiration_date',
        header: 'PA Expiration',
        size: 116,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    { accessorKey: 'lock_status', header: 'Lock Status', size: 154 },
    {
        accessorKey: 'acknowledged_at',
        header: 'Lock Sent',
        size: 170,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    ...commonColumns(activePharmacyData),
];

function getLockDetailsContainer(lockStatusData, activePharmacyData) {
    return lockStatusData?.overrides?.length === 0 && lockStatusData?.auths?.length === 0 ? (
        <div className="padding__around--large">
            <MessagingBlock
                size={MessagingBlock.Sizes.SMALL}
                icon={IconNames.DOCUMENT}
                primaryText="No locks found"
            />
        </div>
    ) : (
        <div className="flex__gap--medium" style={{ flexDirection: 'column' }}>
            <div className="flex__gap--xx-large">
                <DetailLabelValuePair label="PBM" value={lockStatusData?.pbm_name} />
                <DetailLabelValuePair
                    label="Active Referral Assigned Pharmacy"
                    value={activePharmacyData?.organizationName}
                />
            </div>
            {lockStatusData?.overrides?.length > 0 && (
                <div>
                    <DetailLabel label="Override(s)" />
                    <Grid
                        columns={overrideColumns(activePharmacyData)}
                        data={lockStatusData?.overrides}
                    />
                </div>
            )}
            {lockStatusData?.auths?.length > 0 && (
                <div>
                    <DetailLabel label="Prior Auth Lock(s)" />
                    <Grid columns={authColumns(activePharmacyData)} data={lockStatusData?.auths} />
                </div>
            )}
        </div>
    );
}

export const LocksTab = ({ treatmentId, payerId, activeReferralPharmacyNPI = undefined }) => {
    const {
        data: lockStatusData,
        isFetching,
        refetch: refetchLockStatusData,
        fulfilledTimeStamp: lastUpdatedTimestamp,
    } = useCheckLockStatusV2Query({ treatmentId, payerId });

    const { data: activePharmacyData } = useGetSupplierDetailsQuery(
        activeReferralPharmacyNPI
            ? {
                  supplierNpi: activeReferralPharmacyNPI,
              }
            : skipToken,
    );

    return (
        <FormSection
            sectionTitle="Lock Details"
            content={
                isFetching ? (
                    <Spinner className="padding__around--large" size={Spinner.Size.LARGE} />
                ) : (
                    getLockDetailsContainer(lockStatusData, activePharmacyData)
                )
            }
            rightHeaderContent={
                <div className="sds_pbm-lock-status__refresh">
                    {lastUpdatedTimestamp && (
                        <span className="sds_pbm-lock-status__refresh__timestamp">
                            {`Last Updated ${formatDate(lastUpdatedTimestamp, DateFormats.DATE_TIME)}`}
                        </span>
                    )}
                    <ActionButtonText
                        label="Refresh"
                        leftIcon={IconNames.REFRESH}
                        onClick={refetchLockStatusData}
                    />
                </div>
            }
        />
    );
};

LocksTab.propTypes = {
    treatmentId: PropTypes.string.isRequired,
    payerId: PropTypes.string.isRequired,
    activeReferralPharmacyNPI: PropTypes.string,
};
